import config, {Brand} from '../../config';
import thirstyCamelRoundel from '../../images/rewards/camel-club-roundel-thirstycamel.png';
import bottlemartBanner from '../../images/rewards/more-reward-banner-bottlemart.png';
import harryBrownBanner from '../../images/rewards/more-reward-banner-harrybrown.png';
import sipnsaveBanner from '../../images/rewards/more-reward-banner-sipnsave.png';
import thirstyCamelBanner from '../../images/rewards/more-reward-banner-thirstycamel.png';

/** Every banner PNG is 688 x 270; render it at this ratio so nothing crops. */
export const REWARDS_BANNER_ASPECT_RATIO = 688 / 270;

type RewardsBranding = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  banner: any;
  /**
   * The mark on the member card. The banner on every brand except Thirsty
   * Camel, whose card carries the Camel Club roundel (DIS-1861), a 300 x 300
   * export (3x its rendered size) of
   * assets/common/images/thirstycamel/rewards/on-light.png.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cardBadge: any;
  /** Member card surface. Matches the banner's own edge colour, so the banner blends in. */
  cardSurface: string;
  /**
   * Name, "Member since" and staff-pill colour on `cardSurface` (Figma
   * 386-1633): black on SipnSave yellow and Thirsty Camel green, white on the
   * Bottlemart blue and Harry Brown black cards.
   */
  cardText: string;
  /**
   * RewardsBanner's band on SipnSave and Thirsty Camel (DIS-1824). Equal to
   * `cardSurface` except on Thirsty Camel, where DIS-1861 moved the member
   * card to #A2CC73 and the band went back to the theme green #A0CE66.
   */
  liteBannerSurface: string;
  /** OptIn panel under the banner, in the create-account form. */
  optInSurface: string;
};

// SHORTCUT: the colours are literals from the DIS-1765 design. Each
// `cardSurface` except Thirsty Camel's is the banner PNG's edge pixel (sampled
// 2026-09-25; Harry Brown's #000000 re-sampled 2026-10-15 from the Figma
// 377:5257 banner), so a banner and its card cannot drift apart. These are NOT
// the theme's `general.primary` (Bottlemart #0054A6, SipnSave #FFDD00,
// Thirsty Camel #A0CE66), which would leave a visible box around the banner.
// Ceiling: no L0 colour token layer exists. Upgrade path: a design-system
// colour module under app/design-system/tokens/ - DIS-1737 blocks it, DIS-1735
// is the epic.
const bottlemart: RewardsBranding = {
  banner: bottlemartBanner,
  cardBadge: bottlemartBanner,
  cardSurface: '#004797',
  cardText: '#FFFFFF',
  liteBannerSurface: '#004797',
  optInSurface: '#F6FBFF',
};

// SHORTCUT: the per-brand literals below are the ones declared in the SHORTCUT
// above `bottlemart`; same ceiling and upgrade path (DIS-1737, DIS-1735).
const rewardsBrandingByTenant: Record<Brand, RewardsBranding> = {
  [Brand.BOTTLEMART]: bottlemart,
  [Brand.SIPNSAVE]: {
    banner: sipnsaveBanner,
    cardBadge: sipnsaveBanner,
    cardSurface: '#FDE803',
    cardText: '#000000',
    liteBannerSurface: '#FDE803',
    optInSurface: '#F6FBFF',
  },
  [Brand.HARRY_BROWN]: {
    banner: harryBrownBanner,
    cardBadge: harryBrownBanner,
    cardSurface: '#000000',
    cardText: '#FFFFFF',
    liteBannerSurface: '#000000',
    optInSurface: '#F6FBFF',
  },
  // DIS-1861: the card is #A2CC73, which overrides the lime in Figma 386-1633
  // (the ticket says that lime came from mislabelled brand guidelines). The
  // card no longer shows the banner, so it no longer has to match its edge.
  // SHORTCUT: literals, as declared above `bottlemart`.
  [Brand.THIRSTY_CAMEL]: {
    banner: thirstyCamelBanner,
    cardBadge: thirstyCamelRoundel,
    cardSurface: '#A2CC73',
    cardText: '#000000',
    // The Figma lime #94D600 read too fluorescent; this is the theme's
    // `button.primary`, which the band used before DIS-1765.
    liteBannerSurface: '#A0CE66',
    optInSurface: '#F5FFE9',
  },
  [Brand.DEVELOPMENT]: bottlemart,
};

export const rewardsBranding = rewardsBrandingByTenant[config.BASE_TENANT] ?? bottlemart;
